import { TipsterPublic, TipsterTierPublic } from '../types/interfaces';
import { apiGet, apiPost, publicApiGet } from '../utils/api';
import { Sport, League } from './sportsLeaguesService';

export interface TipsterStats {
  total_picks: number;
  total_picks_won: number;
  sum_stake: number;
  total_return: number;
  sum_odds: number;
  picks_with_description: number;
}

export interface SimpleStats {
  total_picks: number;
  total_picks_won: number;
  roi: number | null;
}

export interface TopExpert {
  id: number;
  full_name: string | null;
  country: string | null;
  is_verified: boolean;
  tag_1: string | null;
  tag_2: string | null;
  tag_3: string | null;
  avatar_config?: { color1: string; color2: string; seed: number } | null;
  avatar_matrix?: number[][] | null;
  followers_count: number;
  total_picks: number;
  total_picks_won: number;
  roi: number;
  last_picks?: number[];
}

export interface TopPick {
  tipster_id: number;
  tipster_name: string | null;
  tipster_verified: boolean;
  event: string;
  odds: number;
  home_team: string;
  away_team: string;
  game_datetime: string;
}

export interface TipsterMe {
  id: number;
  user_id: number;
  full_name?: string | null;
  description: string | null;
  appreciation: number;
  is_verified: boolean;
  tag_1: string | null;
  tag_2: string | null;
  tag_3: string | null;
  avatar_config?: { color1: string; color2: string; seed: number } | null;
  avatar_matrix?: number[][] | null;
}

export interface TierSubscriberCount {
  tier_id: number;
  tier_name: string | null;
  subscriber_count: number;
}

export interface EarningsSummary {
  tiers: TierSubscriberCount[];
  this_month: string;
  lifetime: string;
}

export interface PersonalSummary {
  followers: number;
  roi_last_30_days: number | null;
  days_since_last_pick: number | null;
  monetization_active: boolean;
  monetization: EarningsSummary | null;
}

export interface TipsterFilters {
  followingOnly?: boolean;
  tagSearch?: string;
  sortBy?: 'followers' | 'appreciation' | 'recommendations';
}

export interface BetRecommendation {
  id: number;
  bet_event_id: number;
  tipster_id: number;
  tipster_tier_id?: number;
  tipster_description?: string;
  stake?: number;
  bet_event?: {
    id: number;
    odds: number;
    event: string;
    result?: 'WIN' | 'LOOSE' | 'TO_RESOLVE' | 'VOID' | 'UNKNOWN';
    game?: {
      id: number;
      datetime: string;
      home_team: string;
      away_team: string;
      league?: {
        id: number;
        name: string;
      };
    };
  };
  tipster_tier?: {
    id: number;
    level: number;
    name?: string;
    color?: string;
  };
}

export const tipsterApi = {
  getAll: (filters?: TipsterFilters): Promise<TipsterPublic[]> => {
    const params = new URLSearchParams();
    if (filters?.followingOnly) {
      params.append('following_only', 'true');
    }
    if (filters?.tagSearch) {
      params.append('tag_search', filters.tagSearch);
    }
    if (filters?.sortBy) {
      params.append('sort_by', filters.sortBy);
    }
    const queryString = params.toString();
    return apiGet<TipsterPublic[]>(`/tipsters/${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number): Promise<TipsterPublic> => {
    return publicApiGet<TipsterPublic>(`/tipsters/${id}`);
  },
  getPaidTiers: (id: number): Promise<TipsterTierPublic[]> => {
    return publicApiGet<TipsterTierPublic[]>(`/tipsters/${id}/tiers`);
  },
  getRecommendations: (id: number): Promise<BetRecommendation[]> => {
    return publicApiGet<BetRecommendation[]>(`/tipsters/${id}/recommendations`);
  },
  getStats: (id: number): Promise<TipsterStats> => {
    return publicApiGet<TipsterStats>(`/tipsters/${id}/stats`);
  },
  getSimpleStats: (id: number): Promise<SimpleStats> => {
    return publicApiGet<SimpleStats>(`/tipsters/${id}/simple-stats`);
  },
  getSports: (id: number, tier: string = 'free'): Promise<Sport[]> => {
    return publicApiGet<Sport[]>(`/tipsters/${id}/sports?tier=${tier}`);
  },
  getLeagues: (id: number, sportId?: number, tier: string = 'free'): Promise<League[]> => {
    const params = new URLSearchParams({ tier });
    if (sportId) params.set('sport_id', String(sportId));
    return publicApiGet<League[]>(`/tipsters/${id}/leagues?${params.toString()}`);
  },
  getMe: (): Promise<TipsterMe> => {
    return apiGet<TipsterMe>('/tipsters/me');
  },
  getPersonalSummary: (): Promise<PersonalSummary> =>
    apiGet<PersonalSummary>('/tipsters/me/personal-summary'),
  getMyRecommendationsActive: (): Promise<BetRecommendation[]> =>
    apiGet<BetRecommendation[]>('/tipsters/me/recommendations?active_only=true'),
  getLeaderboard: (limit = 10, range: 'all' | '30d' = 'all'): Promise<TopExpert[]> => {
    return publicApiGet<TopExpert[]>(`/tipsters/leaderboard?limit=${limit}&range=${range}`);
  },
  getTopPicks: (limit = 10, days = 3): Promise<TopPick[]> => {
    return publicApiGet<TopPick[]>(`/tipsters/top-picks?limit=${limit}&days=${days}`);
  },
  createCheckout: (tipsterId: number, tierId: number): Promise<{ checkout_url: string }> => {
    return apiPost<{ checkout_url: string }>(`/tipsters/${tipsterId}/tiers/${tierId}/checkout`);
  },
  checkExpertAccess: (tipsterId: number): Promise<{ has_access: boolean; tier_name: string | null; access_end: string | null }> => {
    return apiGet(`/tipsters/${tipsterId}/access`);
  },
};
